import React, { useState, useEffect } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, TextField, Pagination, Typography, Box
} from "@mui/material";
import apiClient from "../../config/axiosConfig"; // Your Axios config

const PublishRecord = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [filters, setFilters] = useState({
    name: "",
    phone: "",
    program: "",
    registeredDate: ""
  });
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    passed_college: "",
    phone: "",
    email: "",
    status: "",
    interested_program: ""
  });

  // Fetch data from backend
  useEffect(() => {
    apiClient.get("/user").then(res => {
      if (res.data && res.data.success) {
        setStudents(res.data.data);
        setFilteredStudents(res.data.data);
      }
    }).catch(err => console.error("Fetch error:", err));
  }, []);

  // Handle filter changes and apply immediately
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);

    const filtered = students.filter(student => {
      return (
        (newFilters.name === "" || student.name.toLowerCase().includes(newFilters.name.toLowerCase())) &&
        (newFilters.phone === "" || student.phone.includes(newFilters.phone)) &&
        (newFilters.program === "" || student.interested_program.toLowerCase().includes(newFilters.program.toLowerCase())) &&
        (newFilters.registeredDate === "" || student.created_date === newFilters.registeredDate)
      );
    });

    setFilteredStudents(filtered);
    setPage(1);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      name: "",
      phone: "",
      program: "",
      registeredDate: ""
    });
    setFilteredStudents(students);
    setPage(1);
  };

  // Delete student
  const deleteStudent = async (uid) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    try {
      const response =await apiClient.delete(`/user/${uid}`);
      if(response.data.success){
        alert(response.data.message);
      }else{
        alert(response.data.message);
      }
      const updated = students.filter(student => student.uid !== uid);
      setStudents(updated);
      setFilteredStudents(updated);
      if (editingId === uid) setEditingId(null);
    } catch (error) {
      console.error("Delete error:", error);
      alert("Failed to delete record.");
    }
  };

  // Start editing a student
  const startEditing = (student) => {
    setEditingId(student.uid);
    setEditForm({
      name: student.name,
      passed_college: student.passed_college,
      phone: student.phone,
      email: student.email,
      status: student.status,
      interested_program: student.interested_program
    });
  };

  // Handle edit form changes
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  // Save edited student
  const saveEdit = async (uid) => {
    try {
        const original = students.find(s => s.uid === uid);
        if (!original) {
        alert("Student record not found.");
        return;
        }

        // Prepare object with only changed fields
        const updatedFields = {};
        for (const key in editForm) {
        if (editForm[key] !== original[key]) {
            updatedFields[key] = editForm[key];
        }
        }

        if (Object.keys(updatedFields).length === 0) {
        alert("No changes detected.");
        return; // Nothing to update
        }


      const response =await apiClient.patch(`/user/record/${uid}`, updatedFields);
      const updated = students.map(s =>
        s.uid === uid ? { ...s, ...updatedFields } : s
      );
      setStudents(updated);
      setFilteredStudents(updated);
      setEditingId(null);
      if(response.data.success === true){

          alert(response.data.message);
    }else{
          alert(response.data.message);

      }
    } catch (error) {
      console.error("Update error:", error);
      alert("Failed to update record.");
    }
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingId(null);
  };

  // Download CSV helper
  const downloadCSV = (data, filename) => {
    if (!data.length) {
      alert("No data available to download.");
      return;
    }
    const csvRows = [];
    const headers = ["UID", "Name", "Passed College", "Phone", "Email", "Registered Date", "OTP", "Status", "Program"];
    csvRows.push(headers.join(","));

    data.forEach(row => {
      csvRows.push([
        row.uid,
        `"${row.name.replace(/"/g, '""')}"`,
        `"${row.passed_college.replace(/"/g, '""')}"`,
        row.phone,
        row.email,
        row.created_date,
        row.otp,
        row.status,
        `"${row.interested_program.replace(/"/g, '""')}"`
      ].join(","));
    });

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.href = url;

    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Dynamic file name for filtered download
  const handleDownloadFiltered = () => {
    const isFilterEmpty = Object.values(filters).every(v => v === '');
    if (isFilterEmpty) {
      alert("Nothing to download.");
      return;
    }
    const activeKeys = Object.keys(filters)
      .filter(key => filters[key] !== '')
      .map(key => key.replace(/([A-Z])/g, '_$1').toLowerCase());

    const dynamicFileName = activeKeys.join("_") || "filtered";
    const fileName = `Filtered_Record(${dynamicFileName}).csv`;
    downloadCSV(filteredStudents, fileName);
  };

  // Download all records
  const handleDownloadAll = () => {
    downloadCSV(students, "All_Records.csv");
  };

  // Pagination slice
  const paginatedData = filteredStudents.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const tableHeads = ["ID", "Student Name", "+2 College", "Contact Number", "Email", "Registered Date", "OTP", "Status (1 allotted)", "Interested Program", "Actions"];

  return (
    <div style={{ padding: "10px" }}>
      {students.length > 0 ? (
        <Paper sx={{ padding: 2 }}>
          {/* Filter Section */}
          <Box display="flex" flexDirection="column" gap={2} mb={2}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "center",
                gap: 5
              }}
            >
              <TextField label="Student Name" name="name" value={filters.name} onChange={handleFilterChange} />
              <TextField label="Phone" name="phone" value={filters.phone} onChange={handleFilterChange} />
              <TextField label="Interested Program" name="program" value={filters.program} onChange={handleFilterChange} />
              <TextField
                type="date"
                label="Registered Date"
                name="registeredDate"
                value={filters.registeredDate}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button variant="outlined" color="secondary" onClick={clearFilters}>Clear Filters</Button>
            </Box>
          </Box>

          {/* Download Buttons */}
          <Box display="flex" gap={2} mb={2}>
            <Button variant="contained" color="success" onClick={handleDownloadFiltered}>Download Filtered Record</Button>
            <Button variant="contained" color="warning" onClick={handleDownloadAll}>Download All</Button>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {tableHeads.map(head => (
                    <TableCell key={head}><strong>{head}</strong></TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map(student => (
                  <TableRow key={student.uid}>
                    <TableCell>{student.uid}</TableCell>

                    {editingId === student.uid ? (
                      <>
                        <TableCell>
                          <TextField
                            size="small"
                            name="name"
                            value={editForm.name}
                            onChange={handleEditChange}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            name="passed_college"
                            value={editForm.passed_college}
                            onChange={handleEditChange}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            name="phone"
                            value={editForm.phone}
                            onChange={handleEditChange}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            name="email"
                            value={editForm.email}
                            onChange={handleEditChange}
                          />
                        </TableCell>
                        <TableCell>{student.created_date}</TableCell>
                        <TableCell>{student.otp}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            name="status"
                            type="number"
                            value={editForm.status}
                            onChange={handleEditChange}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            name="interested_program"
                            value={editForm.interested_program}
                            onChange={handleEditChange}
                          />
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{student.name}</TableCell>
                        <TableCell>{student.passed_college}</TableCell>
                        <TableCell>{student.phone}</TableCell>
                        <TableCell>{student.email}</TableCell>
                        <TableCell>{student.created_date}</TableCell>
                        <TableCell>{student.otp}</TableCell>
                        <TableCell>{student.status}</TableCell>
                        <TableCell>{student.interested_program}</TableCell>
                      </>
                    )}

                    <TableCell sx={{ display: "flex", gap: 1 }}>
                      {editingId === student.uid ? (
                        <>
                          <Button size="small" variant="contained" color="success" onClick={() => saveEdit(student.uid)}>Save</Button>
                          <Button size="small" variant="outlined" color="inherit" onClick={cancelEdit}>Cancel</Button>
                        </>
                      ) : (
                        <>
                          <Button size="small" variant="outlined" color="primary" onClick={() => startEditing(student)}>Edit</Button>
                          <Button size="small" variant="outlined" color="error" onClick={() => deleteStudent(student.uid)}>Delete</Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Pagination
            count={Math.ceil(filteredStudents.length / rowsPerPage)}
            page={page}
            onChange={(e, value) => setPage(value)}
            sx={{ marginTop: 2 }}
          />
        </Paper>
      ) : (
        <Box
          sx={{
            height: "100vh",
            width: "100%",
            backgroundColor: "White",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <Typography
            sx={{
              fontSize: "32px",
              textTransform: "capitalize",
              fontWeight: "600",
              fontFamily: "Roboto Serif"
            }}
          >
            Sorry, No record available.
          </Typography>
        </Box>
      )}
    </div>
  );
};

export default PublishRecord;
